import type * as React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { strings } from "@/I18n";
import { cn } from "@/lib/utils";
import { TEST_IDS } from "@/v2/constants/testIds";
import { Label } from "@/v2/ui/primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/v2/ui/primitives/select";

export interface SelectOption {
  value: string;
  label: string;
}

export interface FormSelectProps {
  name: string;
  label?: string;
  options: SelectOption[];
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

export const FormSelect: React.FC<FormSelectProps> = ({
  name,
  label,
  options,
  placeholder,
  required = false,
  disabled = false,
  className,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const error = errors[name];
  const errorMessage = error?.message as string | undefined;

  return (
    <div
      className={cn("tw-space-y-1", className)}
      data-testid={TEST_IDS.formField(name)}
    >
      {label && (
        <Label htmlFor={name}>
          {label}
          {required && <span className="tw-text-destructive tw-ml-1">*</span>}
        </Label>
      )}
      <Controller
        name={name}
        control={control}
        rules={{
          required: required
            ? strings("validation.fieldRequired", {
                field: label || strings("form.thisField"),
              })
            : false,
        }}
        render={({ field }) => (
          <Select
            value={field.value}
            onValueChange={field.onChange}
            disabled={disabled}
          >
            <SelectTrigger
              id={name}
              className={cn(
                error && "tw-border-destructive focus:tw-ring-destructive"
              )}
            >
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      />
      {errorMessage && (
        <p
          className="tw-text-sm tw-text-destructive tw-mt-1"
          data-testid={TEST_IDS.formError(name)}
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
};
